import React, { useState, useMemo, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../context/LanguageContext";
import styles from "../assets/styles/our-presence.module.css";

const SWIPE_THRESHOLD = 50;

const OurPresence: React.FC = () => {
  const [content, setContent] = useState<any>(null);
  const { t_api } = useLanguage();
  const [current, setCurrent] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const dragStartX = useRef<number | null>(null);
  const headingControls = useAnimation();
  const [refHeading, inViewHeading] = useInView({ triggerOnce: false });
  const underlineControls = useAnimation();
  const [refUnderline, inViewUnderline] = useInView({ triggerOnce: false });

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => setContent(data.Presence || {}))
      .catch(err => console.warn('Presence content unavailable:', err));
  }, []);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 640);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    headingControls.start(inViewHeading ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 });
    underlineControls.start(inViewUnderline ? { scaleX: 1 } : { scaleX: 0 });
  }, [inViewHeading, inViewUnderline, headingControls, underlineControls]);

  const countries = [
    { name: t_api(content?.country_1_name || "OMAN", content?.country_1_name_ar), image: content?.country_1_image || "/OMAN.jpg" },
    { name: t_api(content?.country_2_name || "QATAR", content?.country_2_name_ar), image: content?.country_2_image || "/QATAR.jpg" },
    { name: t_api(content?.country_3_name || "UAE", content?.country_3_name_ar), image: content?.country_3_image || "/UAE.jpg" },
  ];

  const ordered = useMemo(() => {
    const len = countries.length;
    const center = current;
    const left = (current - 1 + len) % len;
    const right = (current + 1) % len;
    return [left, center, right];
  }, [current, countries]);

  const next = () => setCurrent((p) => (p + 1) % countries.length);
  const prev = () => setCurrent((p) => (p - 1 + countries.length) % countries.length);

  const getRole = (idx: number) => {
    if (idx === ordered[1]) return "center";
    if (idx === ordered[0]) return "left";
    if (idx === ordered[2]) return "right";
    return "hidden";
  };

  return (
    <section className="relative pt-14 pb-14 sm:pt-10 sm:pb-10 bg-gray-50 overflow-hidden" style={{ width: "100%" }}>
      <div className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-20"
        style={{ backgroundImage: `url(${content?.bg_image || "/world-map.png"})`, backgroundSize: isMobile ? "auto 80%" : "cover", filter: "grayscale(100%)" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-white/70" />
      <div className="relative container mx-auto px-4 z-10">
        <motion.h2 ref={refHeading} className="text-3xl font-bold text-center text-dark mb-2" initial={{ opacity: 0, y: -30 }} animate={headingControls}>
          {t_api(content?.title || "Our Presence", content?.title_ar)}
        </motion.h2>
        <motion.div ref={refUnderline} className="w-24 h-1 bg-[#F2BB13] mx-auto mb-12 rounded" initial={{ scaleX: 0 }} animate={underlineControls} style={{ transformOrigin: "center" }} />
        <div className={styles.stage} onMouseDown={(e) => { dragStartX.current = e.clientX; }} onMouseUp={(e) => {
          if (dragStartX.current == null) return;
          const delta = e.clientX - dragStartX.current;
          dragStartX.current = null;
          if (Math.abs(delta) < SWIPE_THRESHOLD) return;
          if (delta < 0) next(); else prev();
        }} tabIndex={0}>
          {countries.map((c, i) => {
            const role = getRole(i);
            return (
              <figure key={i} className={[styles.card, role === "center" && styles.center, role === "left" && styles.left, role === "right" && styles.right, role === "hidden" && styles.hidden].filter(Boolean).join(" ")}>
                <img src={c.image} alt={c.name} loading="lazy" draggable={false} className="w-[500px] h-[350px] sm:w-[600px] sm:h-[400px] object-cover rounded-xl shadow-xl" />
              </figure>
            );
          })}
        </div>
        <div className={styles.caption}>
          <h3 className={styles.countryName}>{countries[current].name}</h3>
          <div className={styles.controls}>
            <button onClick={prev}><ChevronLeft size={22} /></button>
            <button onClick={next}><ChevronRight size={22} /></button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurPresence;
